import React, { useState } from 'react';
import { useBookings } from '@/hooks/useBookings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Search, Trash2, Calendar, MapPin, Users, CreditCard, Phone, Mail, CheckCircle, Clock, XCircle, Package, TableIcon, CalendarDays } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookingsCalendar } from '@/components/admin/BookingsCalendar';

const AdminBookings = () => {
  const { bookings, loading, error, updateBookingStatus, deleteBooking } = useBookings();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);

  // Filter bookings based on search term and status
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.booking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.from_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.to_location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.booking_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (bookingId: string, newStatus: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      toast({
        title: "Status Updated",
        description: `Booking status has been updated to ${newStatus}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update booking status.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBooking = async (bookingId: string) => {
    try {
      await deleteBooking(bookingId);
      toast({
        title: "Booking Deleted",
        description: "The booking has been successfully deleted.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete booking.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Confirmed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'cash':
        return <Badge variant="outline">Cash</Badge>;
      case 'paypal':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">PayPal</Badge>;
      case 'stripe':
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Card</Badge>;
      default:
        return <Badge variant="outline">{method}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-destructive">Error loading bookings: {error}</p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Transfer Bookings</h1>
          <p className="text-muted-foreground">Manage all transfer booking requests</p>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{bookings.length}</div>
              <div className="text-sm text-muted-foreground">Total Bookings</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {bookings.filter(b => b.booking_status === 'pending').length}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {bookings.filter(b => b.booking_status === 'confirmed').length}
              </div>
              <div className="text-sm text-muted-foreground">Confirmed</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {bookings.filter(b => b.booking_status === 'cancelled').length}
              </div>
              <div className="text-sm text-muted-foreground">Cancelled</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="table" className="w-full">
        <TabsList>
          <TabsTrigger value="table" className="gap-1.5">
            <TableIcon className="h-4 w-4" /> Tabla
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5">
            <CalendarDays className="h-4 w-4" /> Calendario
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by booking number, customer name, email, or route..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No bookings found matching your criteria.</p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      {booking.booking_number}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Booked on {formatDate(booking.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(booking.booking_status)}
                    {getPaymentMethodBadge(booking.payment_method)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Customer Info */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Customer</h4>
                    <div className="space-y-1">
                      <p className="font-medium">{booking.full_name}</p>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Mail className="w-3 h-3 mr-1" />
                        {booking.email}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Phone className="w-3 h-3 mr-1" />
                        {booking.phone}
                      </div>
                    </div>
                  </div>

                  {/* Trip Details */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Trip Details</h4>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                        {booking.from_location} → {booking.to_location}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Users className="w-3 h-3 mr-1" />
                        {booking.passengers} passenger{booking.passengers > 1 ? 's' : ''}
                      </div>
                      <div className="text-sm text-muted-foreground capitalize">
                        {booking.trip_type} • {booking.service_type}
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Payment</h4>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <CreditCard className="w-3 h-3 mr-1 text-muted-foreground" />
                        <span className="font-semibold">${booking.total_price.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground capitalize">
                        Status: {booking.payment_status}
                      </p>
                      {booking.payment_id && (
                        <p className="text-xs text-muted-foreground">
                          ID: {booking.payment_id}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Flight Info */}
                {(booking.arrival_date || booking.airline) && (
                  <>
                    <Separator className="my-4" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                          <Calendar className="w-3 h-3 mr-1 inline" />
                          Arrival Flight
                        </h4>
                        <div className="text-sm space-y-1">
                          {booking.arrival_date && <p>Date: {booking.arrival_date}</p>}
                          {booking.arrival_time && <p>Time: {booking.arrival_time}</p>}
                          {booking.airline && <p>Airline: {booking.airline}</p>}
                          {booking.flight_number && <p>Flight: {booking.flight_number}</p>}
                        </div>
                      </div>
                      {booking.trip_type === 'roundtrip' && (booking.return_date || booking.return_airline) && (
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                            <Calendar className="w-3 h-3 mr-1 inline" />
                            Return Flight
                          </h4>
                          <div className="text-sm space-y-1">
                            {booking.return_date && <p>Date: {booking.return_date}</p>}
                            {booking.return_time && <p>Time: {booking.return_time}</p>}
                            {booking.return_airline && <p>Airline: {booking.return_airline}</p>}
                            {booking.return_flight_number && <p>Flight: {booking.return_flight_number}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Additional Info */}
                {(booking.amount_of_luggage || booking.remarks || (booking.extra_items && booking.extra_items.length > 0)) && (
                  <>
                    <Separator className="my-4" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {booking.amount_of_luggage && (
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-1">Luggage</h4>
                          <p className="text-sm">{booking.amount_of_luggage}</p>
                        </div>
                      )}
                      {booking.remarks && (
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-1">Remarks</h4>
                          <p className="text-sm">{booking.remarks}</p>
                        </div>
                      )}
                      {/* Extra Items */}
                      {booking.extra_items && booking.extra_items.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                            <Package className="w-3 h-3 mr-1 inline" />
                            Extra Items
                          </h4>
                          <div className="space-y-1">
                            {booking.extra_items.map((item: any, index: number) => (
                              <div key={index} className="text-sm flex justify-between">
                                <span>{item.name} x{item.quantity}</span>
                                <span className="font-medium">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            ))}
                            <div className="text-sm font-bold pt-1 border-t">
                              Extras Total: ${(booking.extras_cost || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Actions */}
                <Separator className="my-4" />
                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <Select
                      value={booking.booking_status}
                      onValueChange={(value) => handleStatusUpdate(booking.id, value as 'pending' | 'confirmed' | 'cancelled')}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Booking</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete booking {booking.booking_number}? 
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteBooking(booking.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
        </TabsContent>

        <TabsContent value="calendar">
          <BookingsCalendar rows={filteredBookings} />
        </TabsContent>
      </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminBookings;